import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import type { WikiNewCategory } from '../types';
import { resolveApiSource } from '../utils/wikiSourceResolver';

// Full hierarchy for breadcrumbs
export interface FullHierarchy {
  // Standard hierarchy
  category?: { id: number; name: string; apiSource: string };
  subCategory?: { id: number; name: string; apiSource: string };
  type?: { id: number; name: string };
  subType?: { id: number; name: string };
  // Skills hierarchy
  race?: { id: number; name: string };
  skillType?: { id: number; name: string };
  class?: { id: number; name: string };
  profession?: { id: number; name: string };
}

// Parameters for findHierarchy
export interface FindHierarchyParams {
  apiSource?: string;
  subcategoryId?: number;
  typeId?: number;
  subtypeId?: number;
  raceId?: number;
  skillTypeId?: number;
  classId?: number;
  professionId?: number;
}

interface WikiTypesContextType {
  categories: WikiNewCategory[];
  setCategories: (categories: WikiNewCategory[]) => void;
  findHierarchy: (params: FindHierarchyParams) => FullHierarchy;
  getSourceChain: (params: FindHierarchyParams) => (string | undefined)[];
}

const WikiTypesContext = createContext<WikiTypesContextType | undefined>(undefined);

export const useWikiTypes = () => {
  const context = useContext(WikiTypesContext);
  if (!context) {
    throw new Error('useWikiTypes must be used within a WikiTypesProvider');
  }
  return context;
};

interface WikiTypesProviderProps {
  children: ReactNode;
}

export const WikiTypesProvider: React.FC<WikiTypesProviderProps> = ({ children }) => {
  const [categories, setCategories] = useState<WikiNewCategory[]>([]);

  // Universal hierarchy search - finds full path based on any available ID
  const findHierarchy = useCallback((params: FindHierarchyParams): FullHierarchy => {
    const { apiSource, subcategoryId, typeId, subtypeId, raceId, skillTypeId, classId, professionId } = params;
    const result: FullHierarchy = {};

    // === SKILLS HIERARCHY ===
    // Search from deepest level (profession) to top (race)
    if (professionId) {
      for (const category of categories) {
        for (const race of category.races || []) {
          for (const skillType of race.skillTypes || []) {
            for (const classItem of skillType.classes || []) {
              for (const profession of classItem.professions || []) {
                if (profession.id === professionId) {
                  result.category = { id: category.id, name: category.name, apiSource: resolveApiSource([category.source]) };
                  result.race = { id: race.id, name: race.name };
                  result.skillType = { id: skillType.id, name: skillType.name };
                  result.class = { id: classItem.id, name: classItem.name };
                  result.profession = { id: profession.id, name: profession.name };
                  return result;
                }
              }
            }
          }
        }
      }
    }

    if (classId) {
      for (const category of categories) {
        for (const race of category.races || []) {
          for (const skillType of race.skillTypes || []) {
            for (const classItem of skillType.classes || []) {
              if (classItem.id === classId) {
                result.category = { id: category.id, name: category.name, apiSource: resolveApiSource([category.source]) };
                result.race = { id: race.id, name: race.name };
                result.skillType = { id: skillType.id, name: skillType.name };
                result.class = { id: classItem.id, name: classItem.name };
                return result;
              }
            }
          }
        }
      }
    }

    if (skillTypeId) {
      for (const category of categories) {
        for (const race of category.races || []) {
          for (const skillType of race.skillTypes || []) {
            if (skillType.id === skillTypeId) {
              result.category = { id: category.id, name: category.name, apiSource: resolveApiSource([category.source]) };
              result.race = { id: race.id, name: race.name };
              result.skillType = { id: skillType.id, name: skillType.name };
              return result;
            }
          }
        }
      }
    }

    if (raceId) {
      for (const category of categories) {
        for (const race of category.races || []) {
          if (race.id === raceId) {
            result.category = { id: category.id, name: category.name, apiSource: resolveApiSource([category.source]) };
            result.race = { id: race.id, name: race.name };
            return result;
          }
        }
      }
    }

    // === STANDARD HIERARCHY ===
    // Search from deepest level (subtype) to top (category)
    if (subtypeId && typeId) {
      for (const category of categories) {
        for (const subCategory of category.subCategories || []) {
          for (const type of subCategory.types || []) {
            if (type.id === typeId) {
              const subType = type.subTypes?.find(st => st.id === subtypeId);
              if (subType) {
                result.category = { id: category.id, name: category.name, apiSource: resolveApiSource([category.source]) };
                result.subCategory = { id: subCategory.id, name: subCategory.name, apiSource: resolveApiSource([category.source, subCategory.source]) };
                result.type = { id: type.id, name: type.name };
                result.subType = { id: subType.id, name: subType.name };
                return result;
              }
            }
          }
        }
      }
    }

    if (typeId) {
      for (const category of categories) {
        for (const subCategory of category.subCategories || []) {
          for (const type of subCategory.types || []) {
            if (type.id === typeId) {
              result.category = { id: category.id, name: category.name, apiSource: resolveApiSource([category.source]) };
              result.subCategory = { id: subCategory.id, name: subCategory.name, apiSource: resolveApiSource([category.source, subCategory.source]) };
              result.type = { id: type.id, name: type.name };
              return result;
            }
          }
        }
      }
    }

    if (subcategoryId) {
      for (const category of categories) {
        for (const subCategory of category.subCategories || []) {
          if (subCategory.id === subcategoryId) {
            result.category = { id: category.id, name: category.name, apiSource: resolveApiSource([category.source]) };
            result.subCategory = { id: subCategory.id, name: subCategory.name, apiSource: resolveApiSource([category.source, subCategory.source]) };
            return result;
          }
        }
      }
    }

    // === FALLBACK: Search by apiSource only (category page) ===
    if (apiSource) {
      for (const category of categories) {
        const categoryApiSource = resolveApiSource([category.source]);
        if (categoryApiSource === apiSource) {
          result.category = { id: category.id, name: category.name, apiSource: categoryApiSource };
          return result;
        }
      }
    }

    return result;
  }, [categories]);

  // Get source chain from hierarchy (for filters/sorting config lookup)
  const getSourceChain = useCallback((params: FindHierarchyParams): (string | undefined)[] => {
    const { apiSource, subcategoryId, typeId, subtypeId, raceId, skillTypeId, classId, professionId } = params;
    const sourceChain: (string | undefined)[] = [];

    // === SKILLS HIERARCHY ===
    if (professionId) {
      for (const category of categories) {
        for (const race of category.races || []) {
          for (const skillType of race.skillTypes || []) {
            for (const classItem of skillType.classes || []) {
              for (const profession of classItem.professions || []) {
                if (profession.id === professionId) {
                  return [category.source, race.source, skillType.source, classItem.source, profession.source];
                }
              }
            }
          }
        }
      }
    }

    if (classId) {
      for (const category of categories) {
        for (const race of category.races || []) {
          for (const skillType of race.skillTypes || []) {
            for (const classItem of skillType.classes || []) {
              if (classItem.id === classId) {
                return [category.source, race.source, skillType.source, classItem.source];
              }
            }
          }
        }
      }
    }

    if (skillTypeId) {
      for (const category of categories) {
        for (const race of category.races || []) {
          for (const skillType of race.skillTypes || []) {
            if (skillType.id === skillTypeId) {
              return [category.source, race.source, skillType.source];
            }
          }
        }
      }
    }

    if (raceId) {
      for (const category of categories) {
        for (const race of category.races || []) {
          if (race.id === raceId) {
            return [category.source, race.source];
          }
        }
      }
    }

    // === STANDARD HIERARCHY ===
    if (subtypeId && typeId) {
      for (const category of categories) {
        for (const subCategory of category.subCategories || []) {
          for (const type of subCategory.types || []) {
            if (type.id === typeId) {
              const subType = type.subTypes?.find(st => st.id === subtypeId);
              if (subType) {
                return [category.source, subCategory.source, type.source, subType.source];
              }
            }
          }
        }
      }
    }

    if (typeId) {
      for (const category of categories) {
        for (const subCategory of category.subCategories || []) {
          for (const type of subCategory.types || []) {
            if (type.id === typeId) {
              return [category.source, subCategory.source, type.source];
            }
          }
        }
      }
    }

    if (subcategoryId) {
      for (const category of categories) {
        for (const subCategory of category.subCategories || []) {
          if (subCategory.id === subcategoryId) {
            return [category.source, subCategory.source];
          }
        }
      }
    }

    // === FALLBACK: Search by apiSource only ===
    if (apiSource) {
      for (const category of categories) {
        const categoryApiSource = resolveApiSource([category.source]);
        if (categoryApiSource === apiSource) {
          return [category.source];
        }
      }
    }

    return sourceChain;
  }, [categories]);

  const value = useMemo<WikiTypesContextType>(() => ({
    categories,
    setCategories,
    findHierarchy,
    getSourceChain
  }), [categories, setCategories, findHierarchy, getSourceChain]);

  return (
    <WikiTypesContext.Provider value={value}>
      {children}
    </WikiTypesContext.Provider>
  );
};
